import { AppForm } from '../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { computed, inject, Injectable, Signal } from '@angular/core';
import { ObrebAppEntityDictionaryObreb, ObrebService, ParcelAppEntityParcelWrite } from '../../../../../../openapi';
import { toSignal } from '@angular/core/rxjs-interop';
import { toNumber } from '../../../../../../services/helpers';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';

@Injectable()
export class ParcelItemForm extends AppForm {

  private obrebService: ObrebService = inject(ObrebService);

  private obrebList: Signal<ObrebAppEntityDictionaryObreb[] | undefined> = toSignal(this.obrebService.getObrebCollectionObrebCollection({
    pagination: false,
    orderName: 'asc'
  }));

  constructor() {
    super({
      precinct: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      registrationNumber: new FormControl('', {
        nonNullable: true,
        validators: [
          CustomValidator.isValidParcelNumber(),
          Validators.required,
          Validators.maxLength(255)
        ]
      }),
      content: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(5000)]
      })
    });
  }

  public registrationNumberChange(v: string): void {
    const code = this.obrebList()?.filter(v => v.name === this.precinct?.value).at(0)?.code as string;
    this.parcelId?.setValue((code || '') + '.' + v);
  }

  public precinctChange(name: string): void {
    this.parcelId?.setValue('');
    this.registrationNumber?.setValue('');
  }

  public obrebListForDisplay(allowedNames: string[]): Signal<ObrebAppEntityDictionaryObreb[] | undefined> {
    if (allowedNames.length > 0) {
      return computed(() => {
        const obrebList = this.obrebList();
        return obrebList?.filter(v => allowedNames.includes(v?.name || ''));
      });
    } else {
      return computed(() => this.obrebList());
    }
  }

  public get content(): FormControl | null {
    return this.get('content') as FormControl | null;
  }

  public get precinct(): FormControl | null {
    return this.get('precinct') as FormControl | null;
  }

  public get registrationNumber(): FormControl | null {
    return this.get('registrationNumber') as FormControl | null;
  }

  public createAccessControl(): void {
    this.addControl('access', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get access(): FormControl | null {
    return this.get('access') as FormControl | null;
  }

  public createDetailedInformationControls(): void {
    this.addControl('landUseClass', new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(255)]
    }));
    this.addControl('maxBuildingCoverage', new FormControl('', {
      nonNullable: true,
      validators: [Validators.max(100)]
    }));
    this.addControl('maxBuildingHeight', new FormControl('', { nonNullable: true, validators: [] }));
    this.addControl('minBiologicalArea', new FormControl('', { nonNullable: true, validators: [] }));
  }

  public createLandUseControl(): void {
    this.addControl('landUse', new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)]
    }));
  }

  public get landUseClass(): FormControl | null {
    return this.get('landUseClass') as FormControl | null;
  }

  public get landUse(): FormControl | null {
    return this.get('landUse') as FormControl | null;
  }

  public get maxBuildingCoverage(): FormControl | null {
    return this.get('maxBuildingCoverage') as FormControl | null;
  }

  public get maxBuildingHeight(): FormControl | null {
    return this.get('maxBuildingHeight') as FormControl | null;
  }

  public get minBiologicalArea(): FormControl | null {
    return this.get('minBiologicalArea') as FormControl | null;
  }

  public createIsEntireParcelCoveredByPlanControl(): void {
    this.addControl('isEntireParcelCoveredByPlan', new FormControl(null, { validators: [Validators.required] }));
  }

  public get isEntireParcelCoveredByPlan(): FormControl | null {
    return this.get('isEntireParcelCoveredByPlan') as FormControl | null;
  }

  public createPlanNameControl(): void {
    this.addControl('planName', new FormControl('', { validators: [Validators.maxLength(255)] }));
  }

  public get planName(): FormControl | null {
    return this.get('planName') as FormControl | null;
  }

  public createParcelIdControl(): void {
    this.addControl('parcelId', new FormControl({ value: '', disabled: true }, {
      nonNullable: true,
      validators: [Validators.maxLength(255)]
    }));
  }

  public get parcelId(): FormControl | null {
    return this.get('parcelId') as FormControl | null;
  }

  public override patchFormValues(value: ParcelAppEntityParcelWrite) {
    super.patchFormValues(value);
    this.landUse?.patchValue(value?.landUseClass);
  }

  public toWriteModel(): ParcelAppEntityParcelWrite {
    return {
      ...this.value,
      parcelId: this.parcelId?.value || null,
      landUseClass: this.value?.landUseClass || this.value?.landUse || null,
      minBiologicalArea: toNumber(this.minBiologicalArea?.value),
      maxBuildingCoverage: toNumber(this.maxBuildingCoverage?.value),
      maxBuildingHeight: toNumber(this.maxBuildingHeight?.value)
    };
  }

}
